/**
 * sibling-swatches.js
 *
 * Web Component for sibling product colour swatches on product cards.
 *
 * Behaviour:
 * - Desktop (hover device): mouseenter swaps card image preview, mouseleave restores,
 *   click navigates to that product URL.
 * - Mobile/touch: click navigates directly (image swap skipped for simplicity).
 * - Active swatch (current product): navigates on click like all others.
 */

class SiblingSwatches extends HTMLElement {
  connectedCallback() {
    // Find the parent product-card custom element
    this._card = this.closest('product-card');
    if (!this._card) return;

    // Target the main product image — rendered by product-media.liquid
    this._img = this._card.querySelector('.product-media__image');
    if (!this._img) return;

    // Store original image sources for restore on mouseleave
    this._originalSrc = this._img.src;
    this._originalSrcset = this._img.srcset || '';

    // Detect pointer type — only enable hover preview on fine pointer devices
    this._isHoverDevice = window.matchMedia('(any-pointer: fine)').matches;

    this.querySelectorAll('.sibling-swatches__item').forEach((item) => {
      if (this._isHoverDevice) {
        item.addEventListener('mouseenter', () => this._previewImage(item));
        item.addEventListener('mouseleave', () => this._restoreImage());
      }
      item.addEventListener('click', () => this._navigate(item));
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') this._navigate(item);
      });
      // Make keyboard accessible
      item.setAttribute('tabindex', '0');
      item.setAttribute('role', 'button');
    });
  }

  _previewImage(item) {
    const imageUrl = item.dataset.imageUrl;
    if (!imageUrl || !this._img) return;
    this._img.src = imageUrl;
    this._img.srcset = ''; // clear srcset so browser uses new src
  }

  _restoreImage() {
    if (!this._img) return;
    this._img.src = this._originalSrc;
    this._img.srcset = this._originalSrcset;
  }

  _navigate(item) {
    const url = item.dataset.productUrl;
    if (url) window.location.href = url;
  }
}

if (!customElements.get('sibling-swatches')) {
  customElements.define('sibling-swatches', SiblingSwatches);
}
